import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { PhoneCall, Play, Loader2, Users } from 'lucide-react';
import { ApptBadge, fmtWhen } from './util';
import { PickList } from './PickList';

const API = process.env.REACT_APP_BACKEND_URL;

const ResumeList = ({ sessions, onResume }) => (!sessions.length ? null : (
  <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="calling-resume-list">
    <p className="mb-2 text-[11px] font-black uppercase tracking-wider text-slate-500">Pick up where you left off</p>
    <ul className="space-y-2">
      {sessions.map((s) => (
        <li key={s.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 px-3 py-2.5" data-testid={`calling-resume-${s.id}`}>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900">{s.label}</p>
            <p className="text-xs text-slate-500">{s.done} of {s.total} called{s.skipped ? ` · ${s.skipped} skipped` : ''} · {s.status === 'active' ? 'In progress' : 'Paused'} {fmtWhen(s.updated_at)}</p>
          </div>
          <button type="button" onClick={() => onResume(s.id)} data-testid={`calling-resume-btn-${s.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#15133A] px-4 py-2 text-xs font-bold text-white"><Play className="h-3.5 w-3.5" /> Resume from client {Math.min(s.cursor + 1, s.total)}</button>
        </li>))}
    </ul>
  </section>
));

export const CallingSetup = ({ headers, onStarted, me, initialSource }) => {
  const [opts, setOpts] = useState(null);
  const [members, setMembers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [source, setSource] = useState(initialSource === 'esign' ? 'esign' : 'portals');
  const [segment, setSegment] = useState('all');
  const [status, setStatus] = useState('');
  const [member, setMember] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(new Set());
  useEffect(() => { setPicked(new Set((preview?.items || []).map((i) => i.key))); }, [preview]);
  const toggle = (k) => setPicked((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  useEffect(() => {
    axios.get(`${API}/api/calling/options`, { headers }).then((r) => setOpts(r.data)).catch(() => {});
    axios.get(`${API}/api/onboarding/team/directory`, { headers }).then((r) => {
      const all = (r.data.users || []).filter((u) => u.role !== 'leads_viewer' && u.is_active !== false);
      setMembers(['admin', 'manager', 'director'].includes(me?.role) ? all : all.filter((u) => u.id === r.data.me));
    }).catch(() => {});
    axios.get(`${API}/api/calling/sessions`, { headers }).then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  }, [headers]);

  const load = useCallback(() => {
    setPreview(null);
    axios.get(`${API}/api/calling/preview`, { headers, params: { source, segment, status: status || undefined, member: member || undefined } }).then((r) => setPreview(r.data)).catch(() => setPreview({ count: 0, items: [] }));
  }, [headers, source, segment, status, member]);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/api/calling/sessions`, { source, segment, status: status || null, member: member || null, keys: [...picked] }, { headers }); onStarted(r.data); }
    catch (e) { setBusy(false); setPreview((p) => ({ ...(p || {}), error: e.response?.data?.detail || 'Could not start' })); }
  };
  const resume = async (sid) => { const r = await axios.post(`${API}/api/calling/sessions/${sid}/resume`, {}, { headers }); onStarted(r.data); };

  return (
    <div className="space-y-4" data-testid="calling-setup">
      <ResumeList sessions={sessions} onResume={resume} />
      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <p className="mb-3 text-[11px] font-black uppercase tracking-wider text-slate-500">Who do you want to call?</p>
        <div className="mb-4 grid grid-cols-2 gap-2" data-testid="calling-sources">
          {[['portals', 'Active Client Portals', 'Clients with an open portal file'], ['esign', 'E-Sign pipeline', 'Everyone still waiting to sign']].map(([k, label, sub]) => (
            <button key={k} type="button" onClick={() => setSource(k)} data-testid={`calling-source-${k}`} className={`rounded-2xl border-2 px-4 py-3 text-left transition-colors ${source === k ? 'border-[#15133A] bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-400'}`}>
              <span className="block text-sm font-black text-slate-900">{label}</span><span className="block text-xs text-slate-500">{sub}</span>
            </button>))}
        </div>
        {source === 'portals' && <div className="flex flex-wrap gap-1.5" data-testid="calling-segments">
          {Object.entries(opts?.segments || {}).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setSegment(k)} data-testid={`calling-segment-${k}`} className={`rounded-full px-3.5 py-2 text-xs font-bold transition-colors ${segment === k ? 'bg-[#15133A] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>{label}</button>))}
        </div>}
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {source === 'portals' && <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="calling-filter-status" className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700">
            <option value="">Any status</option>
            {Object.entries(opts?.statuses || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>}
          <select value={member} onChange={(e) => setMember(e.target.value)} data-testid="calling-filter-member" className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700">
            <option value="">Any broker or team member</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="calling-preview">
        {!preview ? <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Building your queue…</p> : (<>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-4xl font-black tabular-nums text-slate-900" data-testid="calling-preview-count">{preview.count ? picked.size : 0}</p>
              <p className="text-sm text-slate-500"><Users className="mr-1 inline h-4 w-4" />{source === 'esign' ? `signer${preview.count === 1 ? '' : 's'}` : `client${preview.count === 1 ? '' : 's'}`} to call{preview.no_mobile ? ` · ${preview.no_mobile} without a mobile number` : ''}</p>
            </div>
            <button type="button" disabled={!picked.size || busy} onClick={start} data-testid="calling-start-btn" className="fixed inset-x-3 bottom-4 z-30 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 py-3 text-base font-black text-white shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 disabled:opacity-40 sm:static sm:inset-auto">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <PhoneCall className="h-5 w-5" />} Start Calling
            </button>
          </div>
          {preview.error && <p className="mt-2 text-sm font-semibold text-rose-600" data-testid="calling-start-error">{preview.error}</p>}
          {!!preview.items?.length && <PickList items={preview.items} picked={picked} onToggle={toggle} onAll={() => setPicked(new Set(preview.items.map((i) => i.key)))} onNone={() => setPicked(new Set())} />}
          {!!preview.with_appointments && (
            <div className="mt-3 rounded-xl bg-violet-50 p-3 text-xs text-violet-800" data-testid="calling-preview-appointments">
              <p className="font-bold">{preview.with_appointments} client{preview.with_appointments === 1 ? ' has' : 's have'} an upcoming appointment. They're flagged in the queue so you can skip them.</p>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">{preview.items.filter((i) => i.next_appointment_at).map((i) => <li key={i.application_id} className="flex items-center gap-1">{i.name} <ApptBadge at={i.next_appointment_at} /></li>)}</ul>
            </div>)}
        </>)}
      </section>
    </div>
  );
};
